import MicIcon from '@material-ui/icons/Mic';
import MicOffIcon from '@material-ui/icons/MicOff';
import SpeakerIcon from '@material-ui/icons/Speaker';
import { IconButton } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Grow from '@material-ui/core/Grow';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import MenuList from '@material-ui/core/MenuList';
import React from 'react';
import styles from './styles.js';
import useDevices from '../../hooks/useDevices';

export default function CombineAudioButton({
  toggleAudio,
  hasAudio,
  classes,
  getAudioSource,
  cameraPublishing,
  changeAudioSource,
  changeAudioOutput,
  getCurrentAudioOutput,
}) {
  const title = hasAudio ? 'Disable Microphone' : 'Enable Microphone';
  const localClasses = styles();

  const { deviceInfo } = useDevices();
  const [devicesAvailable, setDevicesAvailable] = React.useState(null);
  const [audioOptions, setAudioOptions] = React.useState([]);
  const [audioOutputOptions, setAudioOutputOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [subMenuOpen, setSubMenuOpen] = React.useState(false);
  const [subMenuAnchorEl, setSubMenuAnchorEl] = React.useState(null);
  const anchorRef = React.useRef(null);
  const [selectedAudioIndex, setSelectedAudioIndex] = React.useState(0);
  const [selectedOutputIndex, setSelectedOutputIndex] = React.useState(0);
  const [audioDeviceId, setAudioDeviceId] = React.useState('');
  const [audioOutputId, setAudioOutputId] = React.useState('');

  React.useEffect(() => {
    setDevicesAvailable(deviceInfo.audioInputDevices);
    if (cameraPublishing) {
      const currentDeviceId = getAudioSource()?.deviceId;
      getAudioSource().then((id) => setAudioDeviceId(id));
      getCurrentAudioOutput().then((id) => setAudioOutputId(id));

      const indexOfSelectedAudio = devicesAvailable?.indexOf(
        devicesAvailable?.find((e) => e.deviceId === currentDeviceId)
      );

      const indexOfSelectedOutput = deviceInfo.audioOutputDevices?.indexOf(
        deviceInfo.audioOutputDevices?.find((e) => e.deviceId === audioOutputId)
      );

      setSelectedAudioIndex(indexOfSelectedAudio);
      setSelectedOutputIndex(indexOfSelectedOutput);
    }
  }, [
    cameraPublishing,
    getAudioSource,
    getCurrentAudioOutput,
    deviceInfo,
    devicesAvailable,
  ]);

  React.useEffect(() => {
    if (devicesAvailable) {
      const audioDevicesAvailable = devicesAvailable?.map((e) => e.label);
      setAudioOptions(audioDevicesAvailable);
    }
    if (deviceInfo.audioOutputDevices) {
      const audioOutputsAvailable = deviceInfo.audioOutputDevices.map(
        (e) => e.label
      );
      setAudioOutputOptions(audioOutputsAvailable);
    }
  }, [devicesAvailable, deviceInfo]);

  const handleChangeAudioSource = (event, index) => {
    setSelectedAudioIndex(index);
    setOpen(false);
    setSubMenuOpen(false);
    setSubMenuAnchorEl(null);
    const audioDeviceId = devicesAvailable?.find(
      (device) => device.label === event.target.textContent
    ).deviceId;
    changeAudioSource(audioDeviceId);
  };

  const handleChangeAudioOutput = (event, index) => {
    setSelectedOutputIndex(index);
    setOpen(false);
    setSubMenuOpen(false);
    setSubMenuAnchorEl(null);
    const audioOutputId = deviceInfo.audioOutputDevices.find(
      (device) => device.label === event.target.textContent
    ).deviceId;
    changeAudioOutput(audioOutputId);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
    setSubMenuOpen(false);
    setSubMenuAnchorEl(null);
  };

  const handleClose = (event) => {
    if (
      anchorRef.current &&
      (anchorRef.current.contains(event.target) ||
        subMenuAnchorEl?.contains(event.target))
    ) {
      return;
    }
    setOpen(false);
    setSubMenuOpen(false);
    setSubMenuAnchorEl(null);
  };

  const handleSubMenuOpen = (event) => {
    setSubMenuAnchorEl(event.currentTarget);
    setSubMenuOpen(true);
  };

  const handleSubMenuClose = () => {
    setSubMenuOpen(false);
  };

  return (
    <>
      <ButtonGroup
        disableElevation
        className={classes.groupButton}
        variant='contained'
        ref={anchorRef}
        aria-label='split button'
      >
        <Tooltip title={title} aria-label='add'>
          <IconButton
            edge='start'
            color='inherit'
            aria-label='mic'
            onClick={toggleAudio}
            className={`${classes.arrowButton} ${
              !hasAudio ? classes.disabledButton : ''
            }`}
          >
            {!hasAudio ? (
              <MicOffIcon fontSize='inherit' />
            ) : (
              <MicIcon fontSize='inherit' />
            )}
          </IconButton>
        </Tooltip>
        <IconButton
          size='small'
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label='select merge strategy'
          aria-haspopup='menu'
          onClick={handleToggle}
          className={classes.arrowButton}
        >
          <ArrowDropDownIcon />
        </IconButton>
      </ButtonGroup>

      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        style={{ zIndex: 101 }}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id='split-button-menu'>
                  <MenuItem
                    onMouseEnter={handleSubMenuOpen}
                    onMouseLeave={handleSubMenuClose}
                  >
                    Audio Input
                  </MenuItem>
                  <MenuItem
                    onMouseEnter={handleSubMenuOpen}
                    onMouseLeave={handleSubMenuClose}
                  >
                    Audio Output
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>

      <Popper
        open={subMenuOpen}
        anchorEl={subMenuAnchorEl}
        placement='right-start'
        transition
        disablePortal
        style={{ zIndex: 102 }}
        onMouseEnter={() => setSubMenuOpen(true)}
        onMouseLeave={handleSubMenuClose}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <MenuList>
                {subMenuAnchorEl?.textContent === 'Audio Input'
                  ? audioOptions.map((option, index) => (
                      <MenuItem
                        key={option}
                        selected={index === selectedAudioIndex}
                        onClick={(event) =>
                          handleChangeAudioSource(event, index)
                        }
                        classes={{
                          selected: localClasses.selected,
                          root: localClasses.root,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))
                  : audioOutputOptions.map((option, index) => (
                      <MenuItem
                        key={option}
                        selected={index === selectedOutputIndex}
                        onClick={(event) =>
                          handleChangeAudioOutput(event, index)
                        }
                        classes={{
                          selected: localClasses.selected,
                          root: localClasses.root,
                        }}
                      >
                        {option}
                      </MenuItem>
                    ))}
              </MenuList>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  );
}
